/**
 * Feature 1 (#157): React Query hooks for Agreement Interactions.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { agreementInteractionsApi } from '../api/agreementInteractions';
import { ApiError } from '../api/client';
import type {
  AgreementInteraction, AgreementInteractionCreateInput,
  AgreementInteractionFilters, AgreementInteractionUpdateInput,
  Page, UUID, UserRole,
} from '../api/types';

export interface StaffOption {
  id: UUID;
  display_name: string;
  role: UserRole;
}

export function useStaffOptions() {
  return useQuery<StaffOption[], ApiError>({
    queryKey: ['agreement-interactions', 'staff-options'],
    queryFn: () => api.get<StaffOption[]>('/agreement-interactions/staff-options'),
    staleTime: 5 * 60_000,  // staff list rarely changes
  });
}

const keys = {
  all: ['agreement-interactions'] as const,
  list: (agreementId?: UUID) => ['agreement-interactions', 'list', agreementId] as const,
  detail: (id: UUID) => ['agreement-interactions', 'detail', id] as const,
};

export function useAgreementInteractions(
  agreementId: UUID | undefined,
  opts?: {
    include_deleted?: boolean; filters?: AgreementInteractionFilters;
    /** Paging, passed through to the server (the tab was capped at 50, C-374). */
    limit?: number; offset?: number;
  },
) {
  const includeDeleted = !!opts?.include_deleted;
  // #41 (2026-06-12): filters participate in the query key so every
  // distinct filter combination has its own cache entry (C-131 lesson:
  // identical keys for different views serve stale rows).
  const filters = opts?.filters ?? {};
  const limit = opts?.limit ?? 50;
  const offset = opts?.offset ?? 0;
  return useQuery<Page<AgreementInteraction>, ApiError>({
    queryKey: ['agreement-interactions', 'list', agreementId,
      { includeDeleted, ...filters, limit, offset }],
    queryFn: () => agreementInteractionsApi.list({
      agreement_id: agreementId!,
      include_deleted: includeDeleted,
      ...filters,
    }, limit, offset),
    enabled: !!agreementId,
    // #41: filter changes produce new query keys; without this the table
    // (and its header filter controls) would flash to the loading state
    // on every keystroke. Previous rows stay visible while refetching.
    placeholderData: keepPreviousData,
  });
}

export function useCreateAgreementInteraction() {
  const qc = useQueryClient();
  return useMutation<AgreementInteraction, ApiError, AgreementInteractionCreateInput>({
    mutationFn: (input) => agreementInteractionsApi.create(input),
    onSuccess: (cr) => {
      qc.invalidateQueries({ queryKey: keys.list(cr.agreement_id) });
    },
  });
}

export function useUpdateAgreementInteraction() {
  const qc = useQueryClient();
  return useMutation<AgreementInteraction, ApiError, { id: UUID; input: AgreementInteractionUpdateInput }>({
    mutationFn: ({ id, input }) => agreementInteractionsApi.update(id, input),
    onSuccess: (cr) => {
      qc.invalidateQueries({ queryKey: keys.list(cr.agreement_id) });
      qc.setQueryData(keys.detail(cr.id), cr);
    },
  });
}

export function useDeleteAgreementInteraction() {
  const qc = useQueryClient();
  return useMutation<AgreementInteraction, ApiError, UUID>({
    mutationFn: (id) => agreementInteractionsApi.delete(id),
    onSuccess: (cr) => {
      // Cover both the include_deleted=true and include_deleted=false caches.
      qc.invalidateQueries({ queryKey: ['agreement-interactions', 'list', cr.agreement_id] });
      qc.invalidateQueries({ queryKey: ['change-requests'] });
    },
  });
}

export function useRestoreAgreementInteraction() {
  const qc = useQueryClient();
  return useMutation<AgreementInteraction, ApiError, UUID>({
    mutationFn: (id) => agreementInteractionsApi.restore(id),
    onSuccess: (cr) => {
      qc.invalidateQueries({ queryKey: ['agreement-interactions', 'list', cr.agreement_id] });
      qc.invalidateQueries({ queryKey: ['change-requests'] });
      qc.setQueryData(keys.detail(cr.id), cr);
    },
  });
}
